/**
 * Single source of truth for Hungry Canadian merchant packages.
 *
 * Used by:
 *  - /tools   (profit calculator plan selector)
 *  - /features (full comparison matrix)
 *  - homepage pricing preview
 *
 * Model: month-to-month subscription, 0% food-sales commission, feature-gated
 * (not time-committed). Stripe processing fees apply per order.
 */

export const PACKAGES = [
  {
    id: 'core',
    name: 'Core Starter',
    tagline: 'Tablet-based ordering',
    monthlyFee: 149,
    priceLabel: '$149',
    sub: '/mo',
    setup: 'One-time setup: $199',
    badge: null,
    highlights: [
      '0% commission on food sales',
      'Tablet ordering + QR storefronts',
      'AR & 3D menu listings',
    ],
  },
  {
    id: 'pro',
    name: 'Pro Connect',
    tagline: 'Integrated POS workflows',
    monthlyFee: 249,
    priceLabel: '$249',
    sub: '/mo',
    setup: 'One-time setup: $99 (50% off)',
    badge: 'Most Popular',
    highlights: [
      'Everything in Core Starter',
      'Direct POS integrations',
      'Guided POS mapping + 2 menu refreshes / yr',
    ],
  },
  {
    id: 'growth',
    name: 'Growth VIP',
    tagline: 'High-volume & multi-brand',
    monthlyFee: 399,
    priceLabel: '$399',
    sub: '/mo',
    setup: 'Setup fee waived',
    badge: 'Best Value',
    highlights: [
      'Everything in Pro Connect',
      'Featured storefront + dedicated account manager',
      'Unlimited menu refreshes + annual photo shoot',
    ],
  },
];

// Feature matrix. Each row `values` is ordered [Core Starter, Pro Connect, Growth VIP].
// Values: true = included (✓), false = not included (—), string = specific detail.
export const FEATURE_GROUPS = [
  {
    group: 'Ordering & Fulfillment',
    rows: [
      { label: 'Centralized Marketplace Listing', values: [true, true, true] },
      { label: 'Direct-Link & QR Storefronts', values: [true, true, true] },
      { label: 'Order Dashboard (Web, iOS & Android)', values: [true, true, true] },
      { label: 'Platform-Wide Auto-Printing', hint: 'Thermal kitchen ticket printing', values: [true, true, true] },
      { label: 'Multi-Fulfillment Support', hint: 'Delivery, Pickup, Curbside & Custom', values: [true, true, true] },
      { label: 'Stage-Progress Tracking', values: [true, true, true] },
      { label: 'Guided Fulfillment Configuration', hint: 'In-House, HC Network, Uber Direct', values: [true, true, true] },
      { label: 'Live Customer Delivery Tracking', values: [true, true, true] },
      { label: 'Direct POS Integrations', hint: 'Square, Lightspeed, Clover, Shopify POS, EPOS Now, Deliverect', values: [false, true, true] },
    ],
  },
  {
    group: 'Menu & Merchandising',
    rows: [
      { label: 'Augmented Reality (AR) & 3D Listings', hint: 'Supports GLB, GLTF, SVG & PNG', values: [true, true, true] },
      { label: 'Live Menu Editor & 86-ing Controls', values: [true, true, true] },
      { label: 'Advanced Modifiers & Allergy Tags', values: [true, true, true] },
      { label: 'AI-Powered Menu Descriptions', values: [true, true, true] },
    ],
  },
  {
    group: 'Growth & Operations',
    rows: [
      { label: '0% Food Sales Commission', values: [true, true, true] },
      { label: 'Flexible Promo Engine', hint: 'BOGO, Free Delivery, % Discounts', values: [true, true, true] },
      { label: 'Real-Time Performance Dashboard', values: [true, true, true] },
      { label: 'Exportable CSV Financial Reports', values: [false, false, true] },
      { label: '3-Way Live Order Chat & AI Assistant', values: [true, true, true] },
      { label: 'Role-Based Staff Permissions', values: [true, true, true] },
    ],
  },
  {
    group: 'Onboarding & Support',
    rows: [
      { label: 'One-Time Account Setup Fee', values: ['$199', '$99 (50% off)', '$0 (waived)'] },
      { label: 'Guided POS System Mapping', values: [false, true, true] },
      { label: 'Managed Menu Refresh via Staff', values: ['À la carte', '2 / year', 'Unlimited'] },
      { label: 'Featured Storefront Status', values: [false, false, 'Included'] },
      { label: 'Dedicated Account Manager', values: [false, false, true] },
      { label: 'Professional Menu Photo Shoot', values: ['À la carte', 'À la carte', '1 / year'] },
    ],
  },
];

export const PROCESSING_NOTE =
  'No hidden software surprises: all plans include automatic direct payouts. While Hungry Canadian charges 0% commission on your food sales, standard third-party payment processing fees of 2.9% + $0.30 per transaction apply and are handled automatically through Stripe.';

/**
 * Home Business — a fourth, home-based-kitchen-scaled plan positioned
 * BELOW the 3 main tiers (not part of the standard PACKAGES array so
 * it stays out of the /tools calculator per product decision).
 *
 * Visual: full-width card spanning the width of the 3-column pricing
 * grid, matching the card style of the main tiers.
 */
export const HOME_BUSINESS = {
  id: 'home',
  name: 'Home Business',
  tagline: 'For home-based businesses',
  priceLabel: 'Starting at $14.99',
  sub: '/mo',
  setup: 'One-time setup: $25',
  badge: 'New',
  // Left column of the wide card — what's included
  highlights: [
    'Full platform access, scaled to home-kitchen volumes',
    'QR storefront + Hungry Canadian marketplace listing',
    'Shopify & Square catalog sync — no manual re-entry',
    'Custom promo codes, CSV reports, team permissions',
    'Mobile merchant app for orders on the go',
  ],
  // Right column of the wide card — what's optional / not included
  addons: [
    { label: 'Managed menu refresh', note: 'not included' },
    { label: 'Featured storefront status', note: 'additional cost — speak to a rep' },
    { label: 'Dedicated account manager', note: 'not included (general support provided)' },
    { label: 'Professional menu photo shoot', note: 'available à la carte' },
  ],
};
